import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiFetch } from '../../shared/api'
import type { PlanDogHeaderWire } from './useRegisterPlanDogs'

export interface ChangeHuntDogsVars {
  add: PlanDogHeaderWire[]
  remove: string[]
}

/**
 * Add and/or remove the signed-in user's dogs on a LIVE (ongoing) hunt, posting
 * the denormalized headers to the .NET API's
 * `POST /clubhouse/groups/{groupId}/hunts/{huntId}/dogs`. It writes the live
 * hunt's `hunts/{id}.dogs` the same way {@link useRegisterPlanDogs} writes a
 * plan's — the `{ add, remove }` shape is identical, so the Dogs section's commit
 * logic is unchanged aside from which mutation it calls.
 *
 * Unlike plan registration, this goes through the .NET API rather than a Firebase
 * callable: the live hunt write has its own endpoint, which owns the whole gate
 * (participant/leader, per-dog ownership, refuse-if-ended). `huntId` here is the
 * LIVE hunt's id, which is NOT always the id the open detail query is keyed on:
 * an ongoing hunt is opened by its own id (they match), but a started plan is
 * still viewed under the plan id while its dogs are managed on the live hunt it
 * points at (they differ). So the invalidation targets the group's hunt subtree
 * prefix rather than one id, refreshing whichever detail entry is open.
 */
export function useChangeHuntDogs(
  groupId: string | undefined,
  huntId: string | undefined,
) {
  const call = useApiFetch()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ add, remove }: ChangeHuntDogsVars): Promise<void> => {
      if (!huntId) throw new Error('Missing hunt id for dog change')
      await call<{ status: string }>(
        `/clubhouse/groups/${encodeURIComponent(groupId ?? '')}/hunts/${encodeURIComponent(huntId)}/dogs`,
        { method: 'POST', body: JSON.stringify({ add, remove }) },
      )
    },
    onSuccess: () => {
      // Refresh the open hunt detail so the Dogs section reflects the change.
      // Prefix match covers both the hunt-keyed (ongoing) and plan-keyed
      // (started plan) detail entries, which carry different ids.
      void qc.invalidateQueries({
        queryKey: ['clubhouse', 'groups', groupId, 'hunts'],
      })
    },
  })
}
